// src/app/components/AccountInviteCheck.tsx
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  useToast,
  Box,
  Flex,
  Stack,
  Divider,
} from '@chakra-ui/core';
import { WarningIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import isError from 'lodash/isError';

import isString from 'lodash/isString';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { useUserIsAuthenticated } from '../hooks/useUserIsAuthenticated';
import { useStoreActions, useStoreState } from '../../models/hooks';

const AccountInviteCheck: React.FC = () => {
  const location = useLocation();
  const { currentUserProfile } = useCurrentUserProfile();
  const isAuthenticated = useUserIsAuthenticated();
  const switchAccount = useStoreActions((actions) => actions.account.switchAccount);
  const [isInviteLoading, setIsInviteLoading] = useState(false);

  const currentAccountInvite = useStoreState((state) => state.account.currentAccountInvite);
  const setCurrentAccountInvite = useStoreActions((actions) => actions.account.setCurrentAccountInvite);
  const currentAccountInviteId = currentAccountInvite?.inviteId;
  const currentInvitedAccount = currentAccountInvite?.accountId;
  const currentUserId = currentUserProfile?.id;
  const currentUserAccountId = currentUserProfile?.account;

  const [isOpen, setIsOpen] = React.useState(false);

  const toast = useToast();
  const { t } = useTranslation('team');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteParamEncoded = params.get('inviteParameter');
    if (!inviteParamEncoded) return;

    try {
      const inviteParamDecoded = decodeURIComponent(inviteParamEncoded);
      const inviteParamObject = JSON.parse(inviteParamDecoded) as { accountInviteId?: string; accountId?: string };
      const accountId = inviteParamObject?.accountId ?? '';
      const accountInviteId = inviteParamObject?.accountInviteId ?? '';
      // Validate the structure and types of the parsed object to prevent security vulnerabilities.
      // istanbul ignore else
      if (inviteParamObject && isString(accountInviteId) && isString(accountId)) {
        const newInvite = {
          inviteId: accountInviteId,
          accountId,
        };
        if (currentAccountInviteId !== accountInviteId || currentInvitedAccount !== accountId) {
          setCurrentAccountInvite(newInvite);
        }
      }
    } catch (error) {
      // ignore malformed param
      console.error('Failed to parse invite parameter from URL', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (currentAccountInviteId && isAuthenticated && currentUserId && currentUserAccountId !== currentInvitedAccount) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccountInviteId, currentInvitedAccount, isAuthenticated, currentUserId, currentUserAccountId]);

  const onConfirm = async (): Promise<void> => {
    try {
      setIsInviteLoading(true);
      if (currentAccountInviteId && currentInvitedAccount) {
        await switchAccount({
          accountInviteId: currentAccountInviteId,
          userId: currentUserId as string,
        });
      }
      window.location.reload();
      toast({
        title: t('accountInviteCheck.teamInvitationSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('accountInviteCheck.teamInvitationFailed'),
        description: isError(error) && error.message ? error.message : '',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsInviteLoading(false);
      setIsOpen(false);
      setCurrentAccountInvite({ inviteId: '', accountId: '' });
    }
  };

  const onCancel = (): void => {
    setIsOpen(false);
    setCurrentAccountInvite({ inviteId: '', accountId: '' });
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} isCentered data-testid="account-invite-modal">
      <ModalOverlay />
      <ModalContent maxW="640px" borderRadius="lg" boxShadow="xl">
        <ModalHeader p={4} borderBottomWidth="1px">
          <Flex align="center">
            <Box bg="orange.100" color="orange.500" borderRadius="full" p={2} mr={3} display="inline-flex">
              <WarningIcon />
            </Box>
            <Text as="span" fontSize="lg" fontWeight="bold" data-testid="modal-warning-title">
              {t('accountInviteCheck.warningTitle')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody p={5}>
          <Stack spacing={4}>
            <Text fontWeight="bold" data-testid="modal-body-text-1">
              {t('accountInviteCheck.warningBody')}
            </Text>
            <Text color="red.600" data-testid="modal-body-text-2">
              {t('accountInviteCheck.warningBodyExtra')}
            </Text>
            <Box bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" p={3}>
              <Text data-testid="modal-body-text-3">
                {t('accountInviteCheck.toProceedText')}
                <b>{` ${t('accountInviteCheck.joinCompany')}.`}</b>
              </Text>
            </Box>
          </Stack>
        </ModalBody>
        <Divider />
        <ModalFooter p={4}>
          <Button colorScheme="red" onClick={onCancel} data-testid="InviteCancelBtn">
            {t('accountInviteCheck.cancel')}
          </Button>
          <Button
            colorScheme="blue"
            ml={3}
            onClick={onConfirm}
            data-testid="InviteConfirmBtn"
            isDisabled={isInviteLoading}
            isLoading={isInviteLoading}
          >
            {t('accountInviteCheck.joinCompany')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AccountInviteCheck;
